import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render error in one page so it can't blank the whole app (it did: a Portfolio
 * render loop left a black screen with no sidebar or top bar). The shell stays usable; the page
 * area shows a card with Try again (re-render) and Reload. App keys it by pathname, so moving to
 * another page also clears it.
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[greenbook] page crashed:", error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex h-full items-start justify-center p-4 lg:items-center">
        <div role="alert" className="w-full max-w-md rounded-lg border border-border bg-panel-2 p-5">
          <h2 className="text-base font-semibold text-ink">Something went wrong on this page</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-dim">
            The rest of greenbook still works — try again, reload, or pick another page from the menu.
          </p>
          <p className="mt-3 break-words rounded-md bg-raised px-2 py-1.5 font-mono text-[11px] text-faint" title={error.stack}>
            {error.message || String(error)}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="min-h-11 flex-1 rounded-md border border-border-2 text-[13px] font-semibold text-dim hover:bg-raised"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="min-h-11 flex-1 rounded-md bg-up/20 text-[13px] font-semibold text-up hover:bg-up/30"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
